import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, MapPin, ChevronRight, Inbox, Tag } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminListLeads } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "pending_approval", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "completed", label: "Completed" },
  { key: "declined", label: "Declined" },
  { key: "all", label: "All" },
];

const STATUS_STYLES = {
  pending_approval: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  declined: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABEL = {
  pending_approval: "Pending",
  approved: "Approved",
  completed: "Completed",
  declined: "Declined",
};

function relativeTime(iso) {
  if (!iso) return "";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffS = Math.max(0, Math.round((now - t) / 1000));
  if (diffS < 60) return `${diffS}s ago`;
  const diffM = Math.round(diffS / 60);
  if (diffM < 60) return `${diffM}m ago`;
  const diffH = Math.round(diffM / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString();
}

function scopeSummary(scope) {
  const bits = [];
  if (scope?.sidewalk) bits.push("Sidewalk");
  if (scope?.deck) bits.push("Deck");
  if (scope?.furniture) bits.push("Furniture");
  if (scope?.other) bits.push("Other");
  return bits.join(", ") || "—";
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("pending_approval");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await adminListLeads(tab);
        if (!cancelled) setData(res);
      } catch {
        toast.error("Could not load leads");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const pendingCount = data.counts?.pending_approval ?? 0;

  // Distinct source refs across the currently-loaded set (status-filtered)
  const distinctRefs = useMemo(() => {
    const set = new Set();
    for (const l of data.items) {
      const r = l.source?.ref;
      if (r) set.add(r);
    }
    return Array.from(set).sort();
  }, [data.items]);

  const visibleItems = useMemo(() => {
    if (sourceFilter === "all") return data.items;
    if (sourceFilter === "__direct__") {
      return data.items.filter((l) => !l.source?.ref);
    }
    return data.items.filter((l) => l.source?.ref === sourceFilter);
  }, [data.items, sourceFilter]);

  return (
    <AdminShell>
      <div className="pt-2">
        <div className="flex items-baseline justify-between">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#334E68]">
            Leads
          </h1>
          <span
            data-testid="pending-count-badge"
            className="text-sm text-gray-500"
          >
            <span className="text-[#334E68] font-semibold" data-testid="pending-count-value">
              {pendingCount}
            </span>{" "}
            pending
          </span>
        </div>

        {/* Tabs */}
        <div
          data-testid="status-tabs"
          className="mt-4 -mx-4 px-4 overflow-x-auto no-scrollbar"
        >
          <div className="inline-flex gap-2 min-w-full">
            {STATUS_TABS.map(({ key, label }) => {
              const active = tab === key;
              const count = data.counts?.[key] ?? 0;
              return (
                <button
                  key={key}
                  data-testid={`tab-${key}`}
                  onClick={() => setTab(key)}
                  className={[
                    "min-h-[40px] px-4 rounded-full text-sm font-medium border transition-colors whitespace-nowrap",
                    active
                      ? "bg-[#334E68] text-white border-[#334E68]"
                      : "bg-white text-[#334E68] border-gray-200 hover:border-gray-300",
                  ].join(" ")}
                >
                  {label}{" "}
                  <span
                    className={
                      active
                        ? "text-white/70 ml-1"
                        : "text-gray-400 ml-1"
                    }
                  >
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Source filter */}
        <div className="mt-3 flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-400" />
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger
              data-testid="source-filter-trigger"
              className="min-h-[40px] w-auto min-w-[180px] rounded-full border-gray-200 bg-white text-sm"
            >
              <SelectValue placeholder="Filter by source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="source-option-all">
                All sources
              </SelectItem>
              <SelectItem value="__direct__" data-testid="source-option-direct">
                direct
              </SelectItem>
              {distinctRefs.map((r) => (
                <SelectItem
                  key={r}
                  value={r}
                  data-testid={`source-option-${r}`}
                >
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="py-16 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : visibleItems.length === 0 ? (
            <div
              data-testid="leads-empty"
              className="py-16 text-center text-gray-500"
            >
              <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              No leads in this bucket yet.
            </div>
          ) : (
            visibleItems.map((lead) => (
              <Link
                key={lead.id}
                to={`/admin/leads/${lead.id}`}
                data-testid={`lead-row-${lead.id}`}
                className="block bg-white rounded-2xl border border-gray-200 hover:border-[#16A34A] transition-colors p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#334E68] truncate">
                        {lead.customer?.name}
                      </span>
                      <span
                        className={[
                          "text-xs px-2 py-0.5 rounded-full border font-medium",
                          STATUS_STYLES[lead.status] || "bg-gray-100 text-gray-600 border-gray-200",
                        ].join(" ")}
                      >
                        {STATUS_LABEL[lead.status] || lead.status}
                      </span>
                      {lead.flags?.non_hoboken_zip && (
                        <span
                          data-testid={`non-hoboken-badge-${lead.id}`}
                          className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          Non-Hoboken
                        </span>
                      )}
                      <span
                        data-testid={`source-pill-${lead.id}`}
                        className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 inline-flex items-center gap-1"
                      >
                        <Tag className="w-3 h-3" />
                        {lead.source?.ref || "direct"}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="truncate">
                        {lead.customer?.address} · {lead.customer?.zip}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {scopeSummary(lead.scope)} · {relativeTime(lead.created_at)}
                      </span>
                      <span className="text-sm font-semibold text-[#334E68] whitespace-nowrap">
                        ${lead.estimate?.low}–${lead.estimate?.high}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 mt-1" />
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </AdminShell>
  );
}
